import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import ShowImageDefault from 'app/components/ShowImage/ShowImageDefault';
import { StockLevelAlertIcon } from 'app/components/Status/StockLevelAlertIcon';
import { MenuOptionsDivider } from 'app/components/Table/columns/ActionsCell';
import { ID } from 'app/components/Table/core/types';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import { TippyDefault } from 'app/components/Tippy';
import { MenuAction, MenuItem } from 'app/components/Tippy/TippyMenu';
import Typography from 'app/components/Typography';

import { SensorStatus } from 'app/modules/inventory/bins/components/SensorStatus';
import { DISPLAY_SENSOR_SKU_ID } from 'app/modules/inventory/bins/core/constant';
import { SensorConnectionStatus, SensorSkuId } from 'app/modules/inventory/bins/core/enum';
import { UnableToRemoveItemModal } from 'app/modules/inventory/bins/modal/UnableToRemoveItem.modal';
import ExpiryContent from 'app/modules/inventory/racks/components/ExpiryContent';
import { HIGHLIGHT_EXPIRY_STATUS } from 'app/modules/inventory/racks/core/constants';
import { getStockLevel } from 'app/modules/inventory/stock-run/core/helpers';
import {
  downloadTrasaction,
  downloadTrasactionLedger,
} from 'app/modules/system-settings/devices/core/requests';

import { coverPath } from 'app/helpers';
import { representativeExpiryStatus, useNavigateWithSendPath } from 'app/helpers/utils';
import { useBoolean } from 'app/hooks';
import { isEmpty, isNull } from 'lodash';

import { PATH } from 'app/constants/path';

import { ReactComponent as IconThreeDots } from 'app/assets/icons/icon-3-dots.svg';

import LabelStockTypeCard from '../../items/components/LabelStockTypeCard';
import { ActionBin } from '../../racks/core/enum';
import { curWardSelector, useRackStore } from '../../racks/store';
import { Bin } from '../core/type';

const MENU_CONTAINER_WIDTH = 160;
const TOTAL_PADDING = 32;
const CUSTOM_OPTION_CONTENT_WIDTH = MENU_CONTAINER_WIDTH - TOTAL_PADDING;

export const BinItem = styled.div<{ $isEditLayout?: boolean }>`
  position: relative;
  padding: 12px;
  border: ${({ $isEditLayout }) => ($isEditLayout ? 'none' : '1px solid #d9d9d9')};
  border-radius: 8px;
  position: relative;
  min-height: 136px;
  min-width: 166px;

  .action {
    opacity: 0;
  }
  &:hover .action {
    opacity: 1;
  }
  .action-menu {
    position: absolute;
    top: 8px;
    right: 12px;
    z-index: 2;
  }
`;

export const BorderItem = styled.div`
  display: flex;
  background: #f9fafb;
  padding: 8px;
  border-radius: 6px;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
`;

export const ButtonAction = styled.div`
  width: 32px;
  height: 32px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.06);
  background: var(--white);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: absolute;
  right: 6px;
  top: 6px;
`;

interface Props {
  bin: Bin;
  onClickModal: (key: ActionBin, originBin: Bin) => void;
  inSession?: boolean;
  isDraggedOver?: boolean;
  isEditLayout?: boolean;
  handleRemoveItemOrSensor?: (binId: ID, itemId: ID, sensorId: ID) => void;
  onRemoveExpiredSensor?: (binId: ID, sensorId: ID) => void;
  onRemoveExpirySensor?: (binId: ID) => void;
  stockValue?: {
    restockValue: number;
    alarmValue: number;
    defaultParValue: number;
  };
}
const BinDetailCard: React.FC<Props> = ({
  bin,
  onClickModal,
  inSession,
  isDraggedOver,
  isEditLayout,
  handleRemoveItemOrSensor,
  onRemoveExpirySensor,
}) => {
  const navigate = useNavigateWithSendPath();

  const wardId = useRackStore(curWardSelector);

  const isOpenAction = useBoolean(true);
  const unableToRemoveItem = useBoolean(false);

  const isQuantityLargeThanZero = bin?.item?.qty && bin?.item?.qty > 0;

  const onChangeMenuItem = (action: ActionBin, binItem: Bin) => (event: any) => {
    if (
      [
        ActionBin.assignItem,
        ActionBin.removeItem,
        ActionBin.updateQuantity,
        ActionBin.assignSensor,
        ActionBin.removeSensor,
        ActionBin.assignSensorExpired,
        ActionBin.removeSensorExpired,
        ActionBin.updateBaseWeight,
      ].includes(action) &&
      inSession
    ) {
      return toast.error(
        'This function has been momentarily suspended due to an active tablet session. Please try again later.',
      );
    }

    event.preventDefault();
    event.stopPropagation();
    isOpenAction.setValue(false);
    onClickModal(action, binItem);
  };

  const onClickBin = () => {
    if (bin.item?.id) {
      navigate(PATH.ITEMS_DETAIL.replace(':id', String(bin.item.id)));
    }
  };

  const highlightedExpiryStatus = HIGHLIGHT_EXPIRY_STATUS[representativeExpiryStatus(bin.expiry)];

  const { mutate: mutateDownloadBin, isLoading: isDownloadingBin } = useMutation({
    mutationKey: ['download-bin'],
    mutationFn: (binParams: { binId: ID }) => downloadTrasaction(binParams),
  });

  const { mutate: mutateDownloadBinLedger, isLoading: isDownloadingBinLedger } = useMutation({
    mutationKey: ['download-bin-ledger'],
    mutationFn: (binParams: { binId: ID }) => downloadTrasactionLedger(binParams),
  });

  const handleDownloadBin = () => {
    isOpenAction.setValue(false);
    mutateDownloadBin({ binId: bin.id });
  };

  const handleDownloadBinLedger = () => {
    isOpenAction.setValue(false);
    mutateDownloadBinLedger({ binId: bin.id });
  };

  const handleTippyOnMount = (instance: any) => {
    Object.assign((instance.popper.firstChild as HTMLElement)?.style, {
      backgroundColor: 'var(--white)',
      boxShadow: '0 0 40px 0 rgb(82 63 105 / 20%)',
    });
  };

  const handleRemoveExpiredSensor = () => {
    onRemoveExpirySensor?.(bin.id);
    isOpenAction.setValue(false);
  };

  return (
    <>
      <BinItem
        $isEditLayout={isEditLayout}
        style={bin.inSession ? { borderColor: 'var(--primary)' } : {}}
      >
        <div className="d-flex align-items-center justify-content-between" style={{ height: 30 }}>
          <div className="d-flex align-items-center gap-2">
            <Typography variant="body-6" color="neutral-10">
              {bin.name}
            </Typography>

            {!isEmpty(bin.expiry) && bin.expiry.every((item) => item.expiryDate) && (
              <TippyDefault containerClass="mb-1" content={<ExpiryContent expiries={bin.expiry} />}>
                <Typography
                  variant="body-8"
                  className="mt-1"
                  style={{ color: highlightedExpiryStatus.color, whiteSpace: 'nowrap' }}
                >
                  {highlightedExpiryStatus.text}
                </Typography>
              </TippyDefault>
            )}
          </div>
          <TippyDefault
            interactive
            arrow={false}
            appendTo={document.body}
            onMount={handleTippyOnMount}
            placement="bottom-end"
            offset={[0, 5]}
            trigger="click"
            allowHTML
            content={
              isOpenAction.value && (
                <MenuAction
                  style={{
                    minWidth: MENU_CONTAINER_WIDTH,
                    padding: 8,
                  }}
                >
                  {isEditLayout && bin.item && (
                    <MenuItem
                      onClick={() => {
                        if (bin?.item?.qty && bin?.item?.qty > 0) {
                          isOpenAction.setValue(false);
                          unableToRemoveItem.setValue(true);
                          return;
                        }
                        handleRemoveItemOrSensor?.(bin.id, bin?.item?.id ?? null, null);
                        isOpenAction.setValue(false);
                      }}
                      style={{ marginBottom: 4 }}
                    >
                      Remove item
                    </MenuItem>
                  )}

                  {isEditLayout && bin.sensor && (
                    <MenuItem
                      onClick={() => {
                        if (isQuantityLargeThanZero) {
                          isOpenAction.setValue(false);
                          unableToRemoveItem.setValue(true);
                          return;
                        }
                        handleRemoveItemOrSensor?.(bin.id, null, bin?.sensor?.id ?? null);
                        isOpenAction.setValue(false);
                      }}
                      style={{ marginBottom: 4 }}
                    >
                      Remove sensor
                    </MenuItem>
                  )}

                  {isEditLayout && bin.expirySensor && (
                    <MenuItem
                      onClick={handleRemoveExpiredSensor}
                      className="mb-1 whitespace-nowrap"
                    >
                      Remove expired sensor
                    </MenuItem>
                  )}

                  {bin.item && !isEditLayout ? (
                    <>
                      <MenuItem
                        onClick={onChangeMenuItem(ActionBin.updateQuantity, bin)}
                        style={{ marginBottom: 4 }}
                      >
                        Edit quantity
                      </MenuItem>
                      <MenuItem
                        onClick={onChangeMenuItem(ActionBin.removeItem, bin)}
                        style={{ marginBottom: 4 }}
                      >
                        Remove item
                      </MenuItem>
                    </>
                  ) : (
                    !isEditLayout && (
                      <MenuItem
                        onClick={onChangeMenuItem(ActionBin.assignItem, bin)}
                        style={{ marginBottom: 4 }}
                      >
                        Assign item
                      </MenuItem>
                    )
                  )}

                  {bin.sensor?.skuId === SensorSkuId.Weight && !isEditLayout ? (
                    <MenuItem
                      onClick={onChangeMenuItem(ActionBin.updateBaseWeight, bin)}
                      style={{ marginBottom: 4 }}
                    >
                      Update base weight
                    </MenuItem>
                  ) : null}

                  {bin.sensor && !isEditLayout ? (
                    <>
                      <MenuItem
                        onClick={onChangeMenuItem(ActionBin.removeSensor, bin)}
                        style={{ marginBottom: 4 }}
                      >
                        Remove sensor
                      </MenuItem>
                    </>
                  ) : (
                    !isEditLayout && (
                      <MenuItem
                        onClick={onChangeMenuItem(ActionBin.assignSensor, bin)}
                        style={{ marginBottom: 4 }}
                      >
                        Assign sensor
                      </MenuItem>
                    )
                  )}

                  {bin.expirySensor && !isEditLayout ? (
                    <>
                      <MenuItem
                        onClick={onChangeMenuItem(ActionBin.removeSensorExpired, bin)}
                        className="mb-1 whitespace-nowrap"
                      >
                        Remove expired sensor
                      </MenuItem>
                    </>
                  ) : (
                    !isEditLayout && (
                      <MenuItem
                        onClick={onChangeMenuItem(ActionBin.assignSensorExpired, bin)}
                        className="mb-1 whitespace-nowrap"
                      >
                        Assign expired sensor
                      </MenuItem>
                    )
                  )}

                  {bin.item && !isEditLayout ? (
                    <>
                      <MenuItem onClick={onChangeMenuItem(ActionBin.shortFor, bin)}>
                        Add to a stock run
                      </MenuItem>
                      <MenuOptionsDivider length={CUSTOM_OPTION_CONTENT_WIDTH} />
                    </>
                  ) : undefined}
                  {isEditLayout ? null : (
                    <MenuItem
                      onClick={() => navigate(PATH.BIN_HISTORY.replace(':id', String(bin.id)))}
                    >
                      Show bin history
                    </MenuItem>
                  )}
                  {!isEditLayout && (
                    <MenuItem className="mt-1" onClick={handleDownloadBin}>
                      Export MQTT
                    </MenuItem>
                  )}
                  {!isEditLayout && (
                    <MenuItem className="mt-1" onClick={handleDownloadBinLedger}>
                      Export Ledger
                    </MenuItem>
                  )}
                  {bin.item?.id && !isEditLayout && (
                    <MenuItem
                      onClick={() =>
                        navigate(
                          coverPath(PATH.REPORT_DETAIL, {
                            wardId: wardId,
                            itemId: bin.item?.id,
                          }),
                        )
                      }
                      style={{ marginTop: 4 }}
                    >
                      Go to item report
                    </MenuItem>
                  )}
                </MenuAction>
              )
            }
          >
            <ButtonAction
              className="action"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                isOpenAction.setValue(true);
              }}
            >
              <IconThreeDots />
            </ButtonAction>
          </TippyDefault>
        </div>

        <div className="d-flex flex-column gap-3 position-relative">
          {isNull(bin.sensor) ? (
            <></>
          ) : (
            <BorderItem className="gap-2 align-items-center">
              <SensorStatus status={bin.sensor?.heartbeatStatus} skuId={bin.sensor?.skuId} />

              <div className="d-flex flex-column">
                <Typography
                  variant="body-4"
                  fontSize={14}
                  lineHeight={23}
                  className="text-overflow-ellipsis"
                  color="neutral-10"
                >
                  {bin.sensor?.code}
                </Typography>

                <Typography
                  variant="body-3"
                  fontSize={12}
                  lineHeight={18}
                  color="neutral-7"
                  className="whitespace-nowrap"
                >
                  {DISPLAY_SENSOR_SKU_ID(bin.sensor?.skuId as SensorSkuId)}
                </Typography>
              </div>
            </BorderItem>
          )}

          {bin.expirySensor && (
            <BorderItem className="gap-2 align-items-center">
              <SensorStatus
                skuId={SensorSkuId.Expiry}
                status={bin.expirySensor.heartbeatStatus ?? SensorConnectionStatus.notReady}
              />

              <div className="d-flex flex-column">
                <Typography
                  variant="body-4"
                  fontSize={14}
                  lineHeight={23}
                  className="text-overflow-ellipsis"
                  color="neutral-10"
                >
                  {bin.expirySensor.name}
                </Typography>

                <Typography
                  variant="body-3"
                  fontSize={12}
                  lineHeight={18}
                  color="neutral-7"
                  className="whitespace-nowrap"
                >
                  {DISPLAY_SENSOR_SKU_ID(SensorSkuId.Expiry)}
                </Typography>
              </div>
            </BorderItem>
          )}

          {bin.item ? (
            <BorderItem>
              <ShowImageDefault
                image={bin.item.image ?? ''}
                styleMain={{
                  minWidth: 32,
                  minHeight: 32,
                  width: 32,
                  height: 32,
                }}
              />
              <div
                className="d-flex flex-column"
                style={{
                  marginLeft: 8,
                  flexGrow: 1,
                  width: 'calc(100% - 40px)', // 32px for image width + 8px for margin
                }}
              >
                <Typography
                  variant="body-2"
                  fontSize={14}
                  lineHeight={20}
                  className="text-ellipsis w-100"
                  onClick={onClickBin}
                >
                  {bin.item.name}
                </Typography>
                <LabelStockTypeCard stockType={Number(bin.item.stockType)} />
                <Typography
                  variant="body-3"
                  fontSize={12}
                  lineHeight={18}
                  color="neutral-7"
                  className="whitespace-nowrap mt-1"
                  onClick={onClickBin}
                >
                  {bin.item.skuId}
                </Typography>
                {isEditLayout ? null : (
                  <div className="d-flex align-items-center">
                    <Typography
                      variant="title-4"
                      style={{
                        whiteSpace: 'nowrap',
                        marginRight: 4,
                      }}
                    >
                      {`Qty: ${bin.item.qty ?? 0}`}
                    </Typography>
                    <StockLevelAlertIcon
                      stockLevel={bin.item?.stockLevel}
                      marginLeft={0}
                      isScaleSize={false}
                    />
                  </div>
                )}
              </div>
            </BorderItem>
          ) : (
            <Typography>{'No item assigned'}</Typography>
          )}
        </div>
        {bin.inSession && (
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 73,
              height: 23,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'var(--primary-3)',
              borderBottomLeftRadius: 7,
              zIndex: 1,
              borderTopRightRadius: 8,
            }}
          >
            <Typography fontWeight={600} fontSize={13} color="primary">
              In session
            </Typography>
          </div>
        )}
      </BinItem>

      {unableToRemoveItem.value && (
        <UnableToRemoveItemModal
          onClose={() => unableToRemoveItem.setValue(false)}
          show={unableToRemoveItem.value}
        />
      )}
      {isDownloadingBin || isDownloadingBinLedger ? <LoadingCard /> : null}
    </>
  );
};

export default BinDetailCard;
